import * as React from 'react';
import { useState, useMemo } from 'react';
import { ChevronsUpDown, Plus, Loader2 } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { apiFetch } from '@/utils/api';

interface Vendor {
    id: string;
    name: string;
}

interface VendorComboboxProps {
    vendors: Vendor[];
    value: string;
    onValueChange: (value: string) => void;
    onVendorCreated?: (vendor: Vendor) => void;
    placeholder?: string;
    className?: string;
    hasError?: boolean;
}

export function VendorCombobox({
    vendors,
    value,
    onValueChange,
    onVendorCreated,
    placeholder,
    className,
    hasError = false,
}: VendorComboboxProps) {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    // Find the selected vendor
    const selectedVendor = useMemo(() => {
        return vendors.find((vendor) => vendor.id === value);
    }, [vendors, value]);

    // Check if search matches any existing vendor (case-insensitive)
    const exactMatch = useMemo(() => {
        if (!searchValue.trim()) return true;
        return vendors.some(
            (vendor) => vendor.name.toLowerCase() === searchValue.toLowerCase()
        );
    }, [vendors, searchValue]);

    // Handle creating a new vendor
    const handleCreateVendor = async () => {
        if (!searchValue.trim() || isCreating) return;

        setIsCreating(true);
        try {
            const response = await apiFetch('/api/vendors/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: searchValue.trim(),
                }),
            });

            if (response.ok) {
                const newVendor = await response.json();
                onVendorCreated?.(newVendor);
                onValueChange(newVendor.id);
                setOpen(false);
                setSearchValue('');
            } else {
                const error = await response.json();
                console.error('Failed to create vendor:', error);
            }
        } catch (error) {
            console.error('Error creating vendor:', error);
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        'w-52 max-w-52 justify-between font-normal',
                        !value && 'text-muted-foreground',
                        hasError && 'border-red-500 bg-red-50',
                        className
                    )}
                >
                    <span className="truncate">
                        {selectedVendor?.name || placeholder || t('vendors.selectVendor', 'common')}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-0" align="start">
                <Command shouldFilter={true}>
                    <CommandInput
                        placeholder={t('vendors.searchVendor', 'common')}
                        value={searchValue}
                        onValueChange={setSearchValue}
                    />
                    <CommandList>
                        <CommandEmpty>
                            {searchValue.trim() ? (
                                <span className="text-muted-foreground">
                                    {t('vendors.noVendorFound', 'common')}
                                </span>
                            ) : (
                                <span className="text-muted-foreground">
                                    {t('vendors.typeToSearch', 'common')}
                                </span>
                            )}
                        </CommandEmpty>
                        <CommandGroup>
                            {vendors.map((vendor) => (
                                <CommandItem
                                    key={vendor.id}
                                    value={vendor.name}
                                    onSelect={() => {
                                        onValueChange(vendor.id);
                                        setOpen(false);
                                        setSearchValue('');
                                    }}
                                    className={cn(
                                        value === vendor.id && 'bg-accent'
                                    )}
                                >
                                    <span className="truncate">{vendor.name}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                        {/* Show "Add vendor" option when search doesn't match exactly */}
                        {searchValue.trim() && !exactMatch && (
                            <>
                                <CommandSeparator />
                                <CommandGroup forceMount>
                                    <CommandItem
                                        value={`__create_new_vendor_${searchValue.trim()}`}
                                        onSelect={handleCreateVendor}
                                        disabled={isCreating}
                                        className="cursor-pointer"
                                        forceMount
                                    >
                                        {isCreating ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                            <Plus className="mr-2 h-4 w-4" />
                                        )}
                                        <span>
                                            {t('vendors.addNew', 'common')} "{searchValue.trim()}"
                                        </span>
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
